import { useEffect, useState } from 'react'
import { Empty, Loading, Sheet } from './bits'

import { loadProject } from '../lib/db'
import type { LightsOsDocument } from '../types/document'
import { isStrand } from '../types/document'
import { currentApproval, driftFrom, driftWording, pickList } from '../lib/approval'
import { sectionsOf } from '../lib/sections'
import { bulbsForFeet, WIRE_ALLOWANCE } from '../lib/materials'
import { PRODUCTS } from '../lib/products'
import { longDate } from '../lib/paper'
import {
  describeRun,
  NOT_IN_THE_PACKET,
  totalsOf,
  type PacketSection,
} from '../lib/packet'
import type { Visit } from '../lib/visits'
import { windowOf } from '../lib/visits'

/**
 * WHAT ACTUALLY GOES ON THE HOUSE.
 *
 * CREW-02. A quote says what it costs and an approval says what was agreed.
 * Neither says what to put in the van or where the wire goes, and that is the
 * one thing somebody standing on a driveway at seven in the morning needs.
 *
 * EVERY LINE COMES OFF THE APPROVED COPY, never the live design. That is the
 * whole reason the copy exists: a crew picking against live geometry is
 * cutting wire for a roof somebody edited this morning. When the two have
 * drifted apart, the sheet says so at the top rather than quietly describing a
 * different house from the one on the screen.
 *
 * PHONE FIRST. This is read one handed in daylight, so the numbers are big,
 * the load list is at the top where somebody loading a van can see it, and
 * nothing on it is a control except the way out.
 */
export default function Packet({
  jobId,
  who,
  visit,
  onClose,
}: {
  jobId: string
  who: string
  visit: Visit | null
  onClose: () => void
}) {
  const [doc, setDoc] = useState<LightsOsDocument | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading')

  useEffect(() => {
    let alive = true
    loadProject(jobId)
      .then((d) => {
        if (!alive) return
        setDoc(d ?? null)
        setState(d ? 'ready' : 'missing')
      })
      .catch(() => alive && setState('missing'))
    return () => {
      alive = false
    }
  }, [jobId])

  const approved = doc ? currentApproval(doc) : null
  const live = doc ? sectionsOf(doc) : []
  const drift = approved && doc ? driftFrom(approved, live) : []

  /*
   * SPACING COMES OFF THE RUN, NOT OFF A SETTING. Two runs on one house can be
   * different products, and a packet that printed one spacing for the whole
   * job would have somebody hanging C9s at mini spacing on the garage.
   */
  const rows: PacketSection[] = (() => {
    if (!approved || !doc) return []
    const strands = doc.layers.filter(isStrand)
    const bySection = new Map(live.map((s) => [s.id, s]))
    const list = pickList(
      approved,
      (f) => bulbsForFeet(f, PRODUCTS.C9.spacingInches),
      WIRE_ALLOWANCE,
    )
    return list.rows.map((r) => {
      const runIds = bySection.get(r.sectionId)?.runIds ?? []
      const runs = strands
        .filter((l) => runIds.includes(l.id))
        .map((l) =>
          describeRun({
            id: l.id,
            name: l.name,
            product: l.product,
            palette: l.palette,
            patternRepeat: l.patternRepeat,
          }),
        )
      /*
       * The bulb count is recomputed against this run's own spacing when it
       * has one, because the pick list has to pick a single product and a
       * house can carry two.
       */
      const spacing = runs[0]?.spacingInches
      const bulbs = spacing ? bulbsForFeet(r.feet, spacing) : r.bulbs
      return { ...r, id: r.sectionId, bulbs, clips: bulbs, runs }
    })
  })()

  const totals = totalsOf(rows, WIRE_ALLOWANCE)

  return (
    <Sheet
      title={who}
      variant="packet"
      onClose={onClose}
    >
      <div className="packet-body">
        {state === 'loading' && <Loading what="Opening the job" />}
        {state === 'missing' && (
          <Empty kind="wrong">That job could not be opened on this device.</Empty>
        )}

        {state === 'ready' && !approved && (
          <Empty title="Nothing approved on this job yet">
            A packet is the frozen copy of a scope somebody agreed to, and until that happens
            the design can still change under the crew.
          </Empty>
        )}

        {state === 'ready' && approved && (
          <>
            {/*
              THE SHEET CAN BE OUT OF DATE, AND THAT IS THE FIRST THING ON IT.
              CREW-02 asks for the exact approved packet before departure.
              There is nothing to download, so the honest version of that
              safeguard is saying plainly that the design has moved since.
            */}
            {drift.length > 0 && (
              <div className="vsheet-clash">
                <p className="vsheet-clash-head">
                  The design has been edited since this scope was agreed.
                </p>
                <p>{driftWording(drift)}</p>
                <p>
                  Everything below is what was agreed on {longDate(approved.at.slice(0, 10))}.
                  It is not what is on the screen now.
                </p>
              </div>
            )}

            {/*
              THE LOAD LIST IS FIRST, because it is read in a warehouse
              before anybody drives anywhere, and the numbered runs are read
              at the house.
            */}
            <div className="packet-load">
              <p className="ws-eyebrow">What to load</p>
              <div className="packet-figs">
                <span>
                  <b>{totals.bulbs}</b>
                  bulbs
                </span>
                <span>
                  <b>{totals.clips}</b>
                  clips
                </span>
                <span>
                  <b>{totals.wire}</b>
                  ft of wire
                </span>
                <span>
                  <b>{totals.feet}</b>
                  ft on the house
                </span>
              </div>
              <p className="hint">
                Wire includes the allowance, which is why it is more than the run. Nothing here
                is reserved against stock: there is no stock count behind it.
              </p>
            </div>

            {/* Access notes: the gate, the dog, and when they are expecting you. */}
            {visit && (visit.note.trim() || visit.day) && (
              <div className="packet-access">
                <p className="ws-eyebrow">Getting in</p>
                <p className="packet-when">{windowOf(visit)}</p>
                {visit.note.trim() && <p className="packet-note">{visit.note}</p>}
              </div>
            )}

            <p className="ws-eyebrow packet-h">
              {rows.length} {rows.length === 1 ? 'run' : 'runs'}, as agreed
            </p>
            <div className="packet-rows">
              {rows.map((r) => (
                <div key={r.id} className="prun">
                  {/*
                    THE NUMBER IS FOR TALKING. Run four is short is a
                    sentence somebody says on a phone from a ladder. It is a
                    position in the approved list and carries no identity.
                  */}
                  <span className="prun-n" aria-hidden="true">
                    {r.n}
                  </span>
                  <div className="prun-body">
                    <p className="prun-head">
                      {r.label}
                      <em>{Math.round(r.feet)} ft</em>
                      {!r.verified && <b>estimated, not walked</b>}
                    </p>
                    <p className="prun-take">
                      {r.bulbs} bulbs, {r.clips} clips
                    </p>
                    {r.runs.length === 0 ? (
                      <p className="prun-runs muted">
                        Nothing drawn on this section, so there is no product or color on it.
                      </p>
                    ) : (
                      r.runs.map((run) => (
                        <p key={run.id} className="prun-runs">
                          {run.productLabel} at {run.spacingInches} in · {run.paletteLabel}
                          {run.pattern && ` · ${run.pattern}`}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/*
              AND IT IS AT THE BOTTOM RATHER THAN PINNED. On the visit sheet
              the same note is three lines and pinning it is right. Here it
              is four items, and pinned at 390 it took the whole sheet and
              pushed the runs, which are the point, off the screen.
            */}
            <p className="vsheet-unchecked">
              <b>What this packet does not carry.</b> {NOT_IN_THE_PACKET.join(' ')}
            </p>

            <p className="packet-from">
              Agreed by {approved.by} on {longDate(approved.at.slice(0, 10))}
              {approved.packageName ? `, ${approved.packageName}` : ''}.
            </p>
          </>
        )}
      </div>

      <div className="ws-actions">
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Sheet>
  )
}
